// PdfViewer.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

// Pin the worker to the exact version of pdfjs-dist that react-pdf uses so the
// worker can never get out of sync with the API. Using a CDN (jsdelivr with
// cloudflare fallback) avoids shipping the worker as a CRA-managed asset.
pdfjs.GlobalWorkerOptions.workerSrc =
  `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Stable options prevent react-pdf from re-downloading the document on every
// render (which is catastrophic for big files on mobile networks).
const DOCUMENT_OPTIONS = {
  cMapUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/cmaps/`,
  cMapPacked: true,
  standardFontDataUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
  // Enable range requests + streaming so the browser does not have to buffer
  // the whole PDF into memory before any page can render. This is the key to
  // making big files work on low-memory phones.
  disableAutoFetch: true,
  disableStream: false,
  disableRange: false,
};

const isMobile = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(max-width: 820px)').matches ||
    /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || ''));

// Rendering a canvas at the phone's full devicePixelRatio (often 3) costs ~9×
// the CPU of rendering at 1×. For a menu PDF, 2× is visually indistinguishable
// on a phone and dramatically faster — especially on older Androids.
const getRenderDpr = () => {
  if (typeof window === 'undefined') return 1;
  const dpr = window.devicePixelRatio || 1;
  return isMobile() ? Math.min(dpr, 2) : Math.min(dpr, 2.5);
};

const LazyPage = ({ pageNumber, width, dpr, aspectRatio, onAspectRatio, onFirstRender }) => {
  const wrapperRef = useRef(null);
  const [shouldRender, setShouldRender] = useState(pageNumber === 1);

  useEffect(() => {
    const node = wrapperRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setShouldRender(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Render when the page is within ~1.5 screens of the viewport so it
          // is ready by the time the user scrolls to it, then drop it again
          // once it is far away to reclaim memory.
          setShouldRender(entry.isIntersecting);
        });
      },
      { rootMargin: '150% 0px 150% 0px', threshold: 0.01 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const handlePageLoadSuccess = useCallback(
    (page) => {
      const w = page?.originalWidth ?? page?.width;
      const h = page?.originalHeight ?? page?.height;
      if (w > 0 && h > 0) onAspectRatio?.(pageNumber, h / w);
    },
    [onAspectRatio, pageNumber]
  );

  const handleRenderSuccess = useCallback(() => {
    if (pageNumber === 1) onFirstRender?.();
  }, [onFirstRender, pageNumber]);

  // Size the placeholder from the real page aspect ratio when we know it, so
  // short/landscape menus don't sit inside a tall A4-shaped white box.
  const placeholderHeight = aspectRatio
    ? Math.round(width * aspectRatio)
    : Math.round(width * 1.414);

  return (
    <div
      ref={wrapperRef}
      data-page-number={pageNumber}
      style={{
        width,
        height: placeholderHeight,
        margin: '8px auto',
        background: '#f4f4f4',
        boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
        color: '#888',
        fontFamily: 'system-ui, sans-serif',
        fontSize: 14,
      }}
    >
      {shouldRender ? (
        <Page
          pageNumber={pageNumber}
          width={width}
          devicePixelRatio={dpr}
          renderTextLayer={false}
          renderAnnotationLayer={false}
          onLoadSuccess={handlePageLoadSuccess}
          onRenderSuccess={handleRenderSuccess}
          loading={
            <div style={placeholderContentStyle}>Laden…</div>
          }
          error={
            <div style={placeholderContentStyle}>
              Kon pagina {pageNumber} niet laden
            </div>
          }
        />
      ) : (
        <div style={placeholderContentStyle}>Pagina {pageNumber}</div>
      )}
    </div>
  );
};

const placeholderContentStyle = {
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const PdfViewer = ({ restaurantId, slot = '1' }) => {
  const suffix = slot === '1' ? '' : `-${slot}`;
  const pdfUrl = useMemo(
    () =>
      `https://mateza-cloud-storage.ams3.digitaloceanspaces.com/menu/pdf/${restaurantId}${suffix}.pdf`,
    [restaurantId, suffix]
  );

  // react-pdf only re-loads the document when `file` changes by reference, so
  // we keep the `{ url }` object stable across renders.
  const fileProp = useMemo(() => ({ url: pdfUrl, withCredentials: false }), [pdfUrl]);

  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(() =>
    typeof window !== 'undefined' ? Math.min(window.innerWidth, 1200) : 800
  );
  const [numPages, setNumPages] = useState(null);
  const [loadProgress, setLoadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [pageAspects, setPageAspects] = useState({});

  const handleAspectRatio = useCallback((pageNumber, ratio) => {
    setPageAspects((prev) =>
      prev[pageNumber] === ratio ? prev : { ...prev, [pageNumber]: ratio }
    );
  }, []);

  // Track container width so pages render at the right resolution for the
  // screen. Using ResizeObserver avoids a layout on every scroll.
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;
    const update = () => {
      const w = node.clientWidth;
      if (w > 0) setContainerWidth(w);
    };
    update();
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(update);
      ro.observe(node);
      return () => ro.disconnect();
    }
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);

  const handleLoadSuccess = useCallback((pdf) => {
    setNumPages(pdf.numPages);
    setError(null);
    setLoadProgress(1);
  }, []);

  const handleLoadError = useCallback((err) => {
    console.error('PDF load error', err);
    setError(err);
  }, []);

  const handleLoadProgress = useCallback(({ loaded, total }) => {
    if (total > 0) setLoadProgress(Math.min(loaded / total, 0.99));
  }, []);

  const retry = useCallback(() => {
    setError(null);
    setLoadProgress(0);
    setNumPages(null);
    setPageAspects({});
    setAttempt((a) => a + 1);
  }, []);

  // On mobile we render slightly below the device width to leave room for
  // pinch-zoom without horizontal scroll bars appearing.
  const mobile = isMobile();
  const renderWidth = Math.max(
    240,
    Math.floor(mobile ? containerWidth - 8 : Math.min(containerWidth - 16, 1100))
  );
  const renderDpr = useMemo(() => getRenderDpr(), []);

  // Limit how many pages are rendered simultaneously. Because LazyPage already
  // unmounts off-screen pages, this is really only a belt-and-braces cap for
  // the very first render of a huge document on a weak device.
  const pageNumbers = useMemo(() => {
    if (!numPages) return [];
    return Array.from({ length: numPages }, (_, i) => i + 1);
  }, [numPages]);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'fixed',
        inset: 0,
        margin: 0,
        padding: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        background: '#2b2b2b',
        // Allow native pinch-to-zoom so users can zoom into small text.
        touchAction: 'pan-x pan-y pinch-zoom',
      }}
    >
      <Document
        key={attempt}
        file={fileProp}
        options={DOCUMENT_OPTIONS}
        onLoadSuccess={handleLoadSuccess}
        onLoadError={handleLoadError}
        onLoadProgress={handleLoadProgress}
        loading={
          <LoadingOverlay progress={loadProgress} />
        }
        error={<ErrorOverlay onRetry={retry} />}
        noData={<ErrorOverlay onRetry={retry} message="Geen menu gevonden." />}
      >
        {numPages != null && !error &&
          pageNumbers.map((pageNumber) => (
            <LazyPage
              key={`${attempt}-${pageNumber}`}
              pageNumber={pageNumber}
              width={renderWidth}
              dpr={renderDpr}
              aspectRatio={
                pageAspects[pageNumber] ?? pageAspects[1] ?? null
              }
              onAspectRatio={handleAspectRatio}
            />
          ))}
      </Document>
    </div>
  );
};

const overlayStyle = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  color: '#fff',
  fontFamily: 'system-ui, sans-serif',
  gap: 12,
  padding: 24,
  textAlign: 'center',
};

const LoadingOverlay = ({ progress }) => (
  <div style={overlayStyle}>
    <div style={{ fontSize: 16 }}>Menu wordt geladen…</div>
    <div
      style={{
        width: 'min(260px, 70vw)',
        height: 4,
        background: 'rgba(255,255,255,0.2)',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          width: `${Math.round((progress || 0) * 100)}%`,
          height: '100%',
          background: '#fff',
          transition: 'width 150ms linear',
        }}
      />
    </div>
  </div>
);

const ErrorOverlay = ({ onRetry, message = 'Het menu kon niet geladen worden.' }) => (
  <div style={overlayStyle}>
    <div style={{ fontSize: 16 }}>{message}</div>
    <button
      type="button"
      onClick={onRetry}
      style={{
        padding: '10px 18px',
        fontSize: 15,
        borderRadius: 6,
        border: '1px solid #fff',
        background: 'transparent',
        color: '#fff',
        cursor: 'pointer',
      }}
    >
      Opnieuw proberen
    </button>
  </div>
);

export default PdfViewer;
